import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useFunds, useUpdateFund } from "@/hooks/useFunds";
import { useFundAssets, useDeleteFundAsset } from "@/hooks/useFundAssets";
import { useAllCashflows } from "@/hooks/useCashflows";
import { useInvestments } from "@/hooks/useInvestments";
import { useClients } from "@/hooks/useClients";
import { useMe } from "@/hooks/useMe";
import { PageHeader } from "@/components/PageHeader";
import { AssetFormDialog } from "@/components/AssetFormDialog";
import { AssetCashflowDialog } from "@/components/AssetCashflowDialog";
import { CapitalCallsTab } from "@/components/CapitalCallsTab";
import { CapitalDistributionsTab } from "@/components/CapitalDistributionsTab";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";

const ASSET_COLORS = [
  "#1e3a5f", "#1e5f8f", "#1d7fc4", "#3b9fd6",
  "#60b8e0", "#8dd0ec", "#b3e2f5", "#d6f0fb",
];

const MONTHS_EN = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/** Formata data como "Nov/21" */
function fmtMonYY(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return `${MONTHS_EN[d.getUTCMonth()]}/${String(d.getUTCFullYear()).slice(2)}`;
}

/** Formata número em milhões com 1 decimal; null/0 → "-" */
function fmtMil(v: number | string | null | undefined, dash = true): string {
  const n = Number(v ?? 0);
  if (dash && (v == null || n === 0)) return "—";
  return new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);
}

/** Custo de aquisição em parênteses: (44,3) */
function fmtCost(v: number | string | null | undefined): string {
  if (v == null) return "—";
  const n = Number(v);
  if (n === 0) return "—";
  return `(${fmtMil(Math.abs(n))})`;
}
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { toast } from "sonner";
import type { FundAsset } from "@/types";

const ORIGIN_ORDER = ["Onshore", "Offshore", "Prospect On", "Prospect Off"] as const;

const fmtBRL = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

function FundCashflowTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string | number;
}) {
  if (!active || !payload?.length) return null;
  const entries = payload.filter((p) => p.value !== 0);
  const total = entries.reduce((s, p) => s + p.value, 0);
  return (
    <div className="rounded-lg border border-border bg-card shadow-md p-3 text-xs space-y-1 min-w-[200px]">
      <p className="font-semibold text-sm mb-2">Year {label}</p>
      {entries.map((p) => (
        <div key={p.name} className="flex justify-between gap-4">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: p.color }} />
            <span className="text-muted-foreground truncate max-w-[130px]">{p.name}</span>
          </span>
          <span className="font-medium shrink-0">{fmtBRL(p.value)}</span>
        </div>
      ))}
      <div className="flex justify-between gap-4 border-t pt-1 mt-1 font-semibold">
        <span>Total</span>
        <span>{fmtBRL(total)}</span>
      </div>
    </div>
  );
}

export default function FundDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: funds } = useFunds();
  const { data: assets } = useFundAssets(id);
  const { data: allCashflows } = useAllCashflows();
  const { data: investments } = useInvestments();
  const { data: clients } = useClients();
  const { data: me } = useMe();
  const isAdmin = me?.user.isAdmin ?? false;
  const canEditFunds = isAdmin || (me?.permissions?.can_edit_funds ?? false);
  const canEditAssets = isAdmin || (me?.permissions?.can_edit_assets ?? false);

  const updateFund = useUpdateFund();
  const delAsset = useDeleteFundAsset();

  // Header edit state
  const [headerEdit, setHeaderEdit] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({
    start_date: "",
    investment_period_end: "",
    usdbrl_rate: "",
    irr: "",
    average_tenor: "",
  });

  // Asset CRUD state
  const [assetFormOpen, setAssetFormOpen] = useState(false);
  const [editAsset, setEditAsset] = useState<FundAsset | null>(null);
  const [deleteAssetId, setDeleteAssetId] = useState<string | null>(null);
  const [cashflowAsset, setCashflowAsset] = useState<FundAsset | null>(null);
  const [cashflowOpen, setCashflowOpen] = useState(false);

  const fund = funds?.find((f) => f.id === id);

  const myInvestments = (investments ?? []).filter((i) => i.fund_id === id);
  const totalBRL = myInvestments.reduce((s, i) => s + Number(i.amount_brl), 0);
  const totalUSD = myInvestments.reduce((s, i) => s + Number(i.amount_usd), 0);
  const usdBrl = Number(fund?.usdbrl_rate ?? 0) * totalUSD;
  const totalGeral = totalBRL + usdBrl;
  const acquisitionTotal = (assets ?? []).reduce(
    (s, a) => s + Number(a.acquisition_cost ?? 0),
    0
  ) * 1_000_000;
  const pctInvestido = totalGeral > 0 ? (acquisitionTotal / totalGeral) * 100 : 0;

  const clientName = (cid: string) =>
    clients?.find((c) => c.id === cid)?.name ?? "—";

  const clientOrigin = (cid: string) =>
    clients?.find((c) => c.id === cid)?.origin ?? "Onshore";

  // Investors grouped by origin
  const investorsGrouped = useMemo(() => {
    const map = new Map<string, typeof myInvestments>(ORIGIN_ORDER.map((o) => [o, []]));
    myInvestments.forEach((i) => {
      const origin = clientOrigin(i.client_id);
      const bucket = map.get(origin) ?? [];
      bucket.push(i);
      map.set(origin, bucket);
    });
    return ORIGIN_ORDER.map((origin) => ({
      origin,
      investments: map.get(origin) ?? [],
    })).filter((g) => g.investments.length > 0);
  }, [myInvestments, clients]);

  // Cashflow chart data: stacked por ativo
  const { cashflowChartData, cashflowAssets, cashflowDetailData } = useMemo(() => {
    if (!assets || !allCashflows) return { cashflowChartData: [], cashflowAssets: [], cashflowDetailData: [] };
    const assetMap = new Map(assets.map((a) => [a.id, a]));
    const map = new Map<number, Record<string, number>>();
    allCashflows
      .filter((c) => assetMap.has(c.fund_asset_id))
      .forEach((c) => {
        const asset = assetMap.get(c.fund_asset_id)!;
        const amt = Number(c.amount ?? 0);
        const prev = map.get(c.year) ?? {};
        map.set(c.year, {
          ...prev,
          [asset.asset_name]: (prev[asset.asset_name] ?? 0) + amt,
        });
      });
    const chartData = Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([year, byAsset]) => ({ year, ...byAsset }));
    // ativos que têm pelo menos um cashflow não-zero
    const activeAssets = assets.filter((a) =>
      allCashflows.some((c) => c.fund_asset_id === a.id && Number(c.amount ?? 0) !== 0)
    );
    // Dados para o gráfico detalhado: acrescenta totais acumulados
    const costM = Math.abs(assets.reduce((s, a) => s + Number(a.acquisition_cost ?? 0), 0));
    let cum = 0;
    const detailData = chartData.map((d) => {
      const row = d as Record<string, number>;
      const yearTot = activeAssets.reduce((s, a) => s + (row[a.asset_name] ?? 0), 0);
      cum += yearTot;
      return {
        ...d,
        _yearTotal: yearTot,
        _cumulative: cum,
        _pct: costM > 0 ? (cum / costM) * 100 : 0,
        _phantom: 0,
      };
    });
    return { cashflowChartData: chartData, cashflowAssets: activeAssets, cashflowDetailData: detailData };
  }, [assets, allCashflows]);

  if (!fund) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader title="IR Jus Capital" subtitle="Fund not found" />
      </div>
    );
  }

  const startHeaderEdit = () => {
    setHeaderDraft({
      start_date: fund.start_date ? fund.start_date.slice(0, 10) : "",
      investment_period_end: fund.investment_period_end
        ? fund.investment_period_end.slice(0, 10)
        : "",
      usdbrl_rate: fund.usdbrl_rate != null ? String(fund.usdbrl_rate) : "",
      irr: fund.irr != null ? String(fund.irr) : "",
      average_tenor: fund.average_tenor != null ? String(fund.average_tenor) : "",
    });
    setHeaderEdit(true);
  };

  const saveHeader = async () => {
    try {
      await updateFund.mutateAsync({
        id: fund.id,
        start_date: headerDraft.start_date || null,
        investment_period_end: headerDraft.investment_period_end || null,
        usdbrl_rate: headerDraft.usdbrl_rate ? Number(headerDraft.usdbrl_rate) : null,
        irr: headerDraft.irr ? Number(headerDraft.irr) : null,
        average_tenor: headerDraft.average_tenor ? Number(headerDraft.average_tenor) : null,
      });
      toast.success("Fund updated");
    } catch {
      toast.error("Error updating");
    }
    setHeaderEdit(false);
  };

  const handleDeleteAsset = async () => {
    if (!deleteAssetId) return;
    try {
      await delAsset.mutateAsync(deleteAssetId);
      toast.success("Asset removed");
    } catch {
      toast.error("Error removing");
    }
    setDeleteAssetId(null);
  };

  const openAddAsset = () => {
    setEditAsset(null);
    setAssetFormOpen(true);
  };

  const openEditAsset = (a: FundAsset, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditAsset(a);
    setAssetFormOpen(true);
  };

  const openCashflows = (a: FundAsset) => {
    setCashflowAsset(a);
    setCashflowOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title={fund.name}
        subtitle={
          headerEdit
            ? undefined
            : `Start ${formatDate(fund.start_date)} · Inv. End ${formatDate(fund.investment_period_end)}`
        }
        actions={
          canEditFunds ? (
            headerEdit ? (
              <div className="flex gap-2">
                <Button size="sm" onClick={saveHeader} disabled={updateFund.isPending} className="gap-2">
                  <Check className="h-4 w-4" /> Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setHeaderEdit(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={startHeaderEdit} className="gap-2">
                <Pencil className="h-4 w-4" /> Edit
              </Button>
            )
          ) : undefined
        }
      />

      {/* Inline header edit fields */}
      {headerEdit && (
        <div className="border-b border-border bg-muted/30">
          <div className="container mx-auto px-4 py-3 flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Start Date</span>
              <Input
                type="date"
                value={headerDraft.start_date}
                onChange={(e) =>
                  setHeaderDraft((d) => ({ ...d, start_date: e.target.value }))
                }
                className="h-8 w-40"
              />
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Inv. End Date</span>
              <Input
                type="date"
                value={headerDraft.investment_period_end}
                onChange={(e) =>
                  setHeaderDraft((d) => ({ ...d, investment_period_end: e.target.value }))
                }
                className="h-8 w-40"
              />
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">USD/BRL</span>
              <Input
                type="number"
                step="0.0001"
                value={headerDraft.usdbrl_rate}
                onChange={(e) =>
                  setHeaderDraft((d) => ({ ...d, usdbrl_rate: e.target.value }))
                }
                className="h-8 w-28"
              />
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">IRR (%)</span>
              <Input
                type="number"
                step="any"
                value={headerDraft.irr}
                onChange={(e) =>
                  setHeaderDraft((d) => ({ ...d, irr: e.target.value }))
                }
                className="h-8 w-28"
              />
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Tenor (Y)</span>
              <Input
                type="number"
                step="any"
                value={headerDraft.average_tenor}
                onChange={(e) =>
                  setHeaderDraft((d) => ({ ...d, average_tenor: e.target.value }))
                }
                className="h-8 w-28"
              />
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8 space-y-6">
        <h1 className="text-2xl font-display font-semibold">{fund.name}</h1>

        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-[70%]">
          <Card>
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-sm text-muted-foreground">Committed BRL</CardTitle>
            </CardHeader>
            <CardContent className="pb-3 px-4">
              <p className="text-[20px] font-bold">{new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalBRL)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-sm text-muted-foreground">Committed USD</CardTitle>
            </CardHeader>
            <CardContent className="pb-3 px-4">
              <p className="text-[20px] font-bold">{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalUSD)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-sm text-muted-foreground">Total (BRL)</CardTitle>
            </CardHeader>
            <CardContent className="pb-3 px-4">
              <p className="text-[20px] font-bold">{new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalGeral)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-sm text-muted-foreground">% Invested</CardTitle>
            </CardHeader>
            <CardContent className="pb-3 px-4">
              <p className="text-[20px] font-bold">{Math.abs(pctInvestido).toFixed(0)}%</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="visao-geral">
          <TabsList>
            <TabsTrigger value="visao-geral">Overview</TabsTrigger>
            <TabsTrigger value="investors">Investors ({myInvestments.length})</TabsTrigger>
            <TabsTrigger value="assets">Assets ({(assets ?? []).length})</TabsTrigger>
            <TabsTrigger value="cashflow">Cashflow</TabsTrigger>
            <TabsTrigger value="capital-calls">Capital Calls</TabsTrigger>
            <TabsTrigger value="capital-distributions">Capital Distributed</TabsTrigger>
          </TabsList>

          {/* Visão Geral tab */}
          <TabsContent value="visao-geral">
            {(() => {
              const sorted = [...(assets ?? [])].sort((a, b) => {
                const da = a.investment_date ? new Date(a.investment_date).getTime() : 0;
                const db = b.investment_date ? new Date(b.investment_date).getTime() : 0;
                return da - db;
              });

              const totalCost       = sorted.reduce((s, a) => s + Number(a.acquisition_cost ?? 0), 0);
              const totalCapRec     = sorted.reduce((s, a) => s + Number(a.capital_received ?? 0), 0);
              const totalFutRec     = sorted.reduce((s, a) => s + Number(a.future_receivables ?? 0), 0);
              const totalFutVal     = sorted.reduce((s, a) => s + Number(a.future_asset_value ?? 0), 0);
              const weightedAvg = (field: keyof typeof sorted[0]) => {
                if (totalCost === 0) return null;
                const num = sorted.reduce((s, a) => s + Number(a[field] ?? 0) * Number(a.acquisition_cost ?? 0), 0);
                return num / totalCost;
              };
              const avgMultiple = weightedAvg("multiple");

              const firstDates = sorted.map(a => a.first_payment_date).filter(Boolean) as string[];
              const lastDates  = sorted.map(a => a.last_payment_date).filter(Boolean) as string[];
              const earliestFirst = firstDates.length ? firstDates.reduce((a, b) => a < b ? a : b) : null;
              const latestLast    = lastDates.length  ? lastDates.reduce((a, b) => a > b ? a : b)  : null;

              const thStyle: React.CSSProperties = {
                backgroundColor: "rgb(212, 163, 123)",
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "11px",
                padding: "6px 8px",
                whiteSpace: "nowrap",
                textAlign: "center",
              };
              const thRStyle: React.CSSProperties = { ...thStyle, textAlign: "center" };
              const tdStyle: React.CSSProperties = {
                fontSize: "12px",
                padding: "5px 8px",
                whiteSpace: "nowrap",
                borderBottom: "1px solid hsl(var(--border))",
              };
              const tdCStyle: React.CSSProperties = { ...tdStyle, textAlign: "center" };
              const tdRStyle: React.CSSProperties = { ...tdStyle, textAlign: "center" };
              const totStyle: React.CSSProperties = {
                backgroundColor: "rgb(0, 56, 130)",
                color: "#e2eaf5",
                fontWeight: 700,
                fontSize: "12px",
                padding: "6px 8px",
                whiteSpace: "nowrap",
              };
              const totCStyle: React.CSSProperties = { ...totStyle, textAlign: "center" };
              const totRStyle: React.CSSProperties = { ...totStyle, textAlign: "center" };

              return (
                <div className="rounded-lg border border-border overflow-x-auto">
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Asset</th>
                        <th style={thStyle}>Inv. Thesis</th>
                        <th style={thStyle}>Inv. Date</th>
                        <th style={thRStyle}>Acq. Cost</th>
                        <th style={thRStyle}>Cap. Received</th>
                        <th style={thRStyle}>Future Recv.</th>
                        <th style={thRStyle}>Future Asset Val.</th>
                        <th style={thRStyle}>CoC</th>
                        <th style={thRStyle}>IRR</th>
                        <th style={thStyle}>1st Pmt</th>
                        <th style={thRStyle}>1st Pmt. Val.</th>
                        <th style={thStyle}>Last Pmt</th>
                        <th style={thRStyle}>Last Pmt. Val.</th>
                        <th style={thRStyle}>Tenor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((a, idx) => (
                        <tr
                          key={a.id}
                          style={{ backgroundColor: idx % 2 === 0 ? "hsl(var(--card))" : "rgb(242, 242, 242)" }}
                        >
                          <td style={{ ...tdStyle, fontWeight: 600 }}>{a.asset_name}</td>
                          <td style={tdStyle}>{a.thesis ?? "—"}</td>
                          <td style={tdCStyle}>{fmtMonYY(a.investment_date)}</td>
                          <td style={tdRStyle}>{fmtCost(a.acquisition_cost)}</td>
                          <td style={tdRStyle}>{fmtMil(a.capital_received)}</td>
                          <td style={tdRStyle}>{fmtMil(a.future_receivables)}</td>
                          <td style={tdRStyle}>{fmtMil(a.future_asset_value)}</td>
                          <td style={tdRStyle}>{Number(a.multiple) ? `${fmtMil(a.multiple, false)}x` : "—"}</td>
                          <td style={tdRStyle}>{Number(a.irr) ? `${fmtMil(a.irr, false)}%` : "—"}</td>
                          <td style={tdCStyle}>{fmtMonYY(a.first_payment_date)}</td>
                          <td style={tdRStyle}>{fmtMil(a.first_payment_value)}</td>
                          <td style={tdCStyle}>{fmtMonYY(a.last_payment_date)}</td>
                          <td style={tdRStyle}>{fmtMil(a.last_payment_value)}</td>
                          <td style={tdRStyle}>{Number(a.average_tenor) ? `${fmtMil(a.average_tenor, false)}Y` : "—"}</td>
                        </tr>
                      ))}
                      {sorted.length > 0 && (
                        <tr>
                          <td style={{ ...totStyle, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em" }} colSpan={3}>
                            Total ({sorted.length})
                          </td>
                          <td style={totRStyle}>{fmtCost(totalCost)}</td>
                          <td style={totRStyle}>{fmtMil(totalCapRec)}</td>
                          <td style={totRStyle}>{fmtMil(totalFutRec)}</td>
                          <td style={totRStyle}>{fmtMil(totalFutVal)}</td>
                          <td style={totRStyle}>{avgMultiple != null ? `${fmtMil(avgMultiple, false)}x` : "—"}</td>
                          <td style={totRStyle}>{fund.irr != null ? `${fmtMil(fund.irr, false)}%` : "—"}</td>
                          <td style={totCStyle}>{fmtMonYY(earliestFirst)}</td>
                          <td style={totRStyle} />
                          <td style={totCStyle}>{fmtMonYY(latestLast)}</td>
                          <td style={totRStyle} />
                          <td style={totRStyle}>{fund.average_tenor != null ? `${fmtMil(fund.average_tenor, false)}Y` : "—"}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </TabsContent>

          {/* Ativos tab */}
          <TabsContent value="assets">
            {canEditAssets && (
              <div className="flex justify-end mb-3">
                <Button size="sm" onClick={openAddAsset} className="gap-2">
                  <Plus className="h-4 w-4" /> New Asset
                </Button>
              </div>
            )}
            {(() => {
              const sorted = [...(assets ?? [])].sort((a, b) => {
                const da = a.investment_date ? new Date(a.investment_date).getTime() : 0;
                const db = b.investment_date ? new Date(b.investment_date).getTime() : 0;
                return da - db;
              });

              const totalCost   = sorted.reduce((s, a) => s + Number(a.acquisition_cost ?? 0), 0);
              const totalCapRec = sorted.reduce((s, a) => s + Number(a.capital_received ?? 0), 0);
              const totalFutRec = sorted.reduce((s, a) => s + Number(a.future_receivables ?? 0), 0);
              const totalFutVal = sorted.reduce((s, a) => s + Number(a.future_asset_value ?? 0), 0);

              const firstDates = sorted.map(a => a.first_payment_date).filter(Boolean) as string[];
              const lastDates  = sorted.map(a => a.last_payment_date).filter(Boolean) as string[];
              const earliestFirst = firstDates.length ? firstDates.reduce((a, b) => a < b ? a : b) : null;
              const latestLast    = lastDates.length  ? lastDates.reduce((a, b) => a > b ? a : b)  : null;

              const thS: React.CSSProperties = {
                backgroundColor: "rgb(212, 163, 123)",
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "11px",
                padding: "6px 8px",
                whiteSpace: "nowrap",
                textAlign: "center",
              };
              const tdS: React.CSSProperties = {
                fontSize: "12px",
                padding: "5px 8px",
                whiteSpace: "nowrap",
                borderBottom: "1px solid hsl(var(--border))",
              };
              const tdCS: React.CSSProperties = { ...tdS, textAlign: "center" };
              const tdRS: React.CSSProperties = { ...tdS, textAlign: "center" };
              const totS: React.CSSProperties = {
                backgroundColor: "rgb(0, 56, 130)",
                color: "#e2eaf5",
                fontWeight: 700,
                fontSize: "12px",
                padding: "6px 8px",
                whiteSpace: "nowrap",
              };
              const totRS: React.CSSProperties = { ...totS, textAlign: "center" };

              return (
                <div className="rounded-lg border border-border overflow-x-auto">
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={thS}>Asset</th>
                        <th style={thS}>Inv. Thesis</th>
                        <th style={thS}>Inv. Date</th>
                        <th style={{ ...thS, textAlign: "center" }}>Acq. Cost</th>
                        <th style={{ ...thS, textAlign: "center" }}>Cap. Received</th>
                        <th style={{ ...thS, textAlign: "center" }}>Future Recv.</th>
                        <th style={{ ...thS, textAlign: "center" }}>Future Asset Val.</th>
                        <th style={{ ...thS, textAlign: "center" }}>CoC</th>
                        <th style={{ ...thS, textAlign: "center" }}>IRR</th>
                        <th style={thS}>1st Pmt</th>
                        <th style={{ ...thS, textAlign: "center" }}>1st Pmt. Val.</th>
                        <th style={thS}>Last Pmt</th>
                        <th style={{ ...thS, textAlign: "center" }}>Last Pmt. Val.</th>
                        <th style={{ ...thS, textAlign: "center" }}>Tenor</th>
                        {canEditAssets && <th style={{ ...thS, width: "64px" }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((a, idx) => (
                        <tr
                          key={a.id}
                          style={{
                            backgroundColor: idx % 2 === 0 ? "hsl(var(--card))" : "rgb(242, 242, 242)",
                            cursor: "pointer",
                          }}
                          onClick={() => openCashflows(a)}
                        >
                          <td style={{ ...tdS, fontWeight: 600 }}>{a.asset_name}</td>
                          <td style={tdS}>{a.thesis ?? "—"}</td>
                          <td style={tdCS}>{fmtMonYY(a.investment_date)}</td>
                          <td style={tdRS}>{fmtCost(a.acquisition_cost)}</td>
                          <td style={tdRS}>{fmtMil(a.capital_received)}</td>
                          <td style={tdRS}>{fmtMil(a.future_receivables)}</td>
                          <td style={tdRS}>{fmtMil(a.future_asset_value)}</td>
                          <td style={tdRS}>{Number(a.multiple) ? `${fmtMil(a.multiple, false)}x` : "—"}</td>
                          <td style={tdRS}>{Number(a.irr) ? `${fmtMil(a.irr, false)}%` : "—"}</td>
                          <td style={tdCS}>{fmtMonYY(a.first_payment_date)}</td>
                          <td style={tdRS}>{fmtMil(a.first_payment_value)}</td>
                          <td style={tdCS}>{fmtMonYY(a.last_payment_date)}</td>
                          <td style={tdRS}>{fmtMil(a.last_payment_value)}</td>
                          <td style={tdRS}>{Number(a.average_tenor) ? `${fmtMil(a.average_tenor, false)}Y` : "—"}</td>
                          {canEditAssets && (
                            <td
                              style={{ ...tdS, padding: "2px 4px" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex gap-0.5">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => openEditAsset(a, e)}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteAssetId(a.id)}>
                                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                      {sorted.length > 0 && (
                        <tr>
                          <td
                            style={{ ...totS, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em" }}
                            colSpan={3}
                          >
                            Total ({sorted.length})
                          </td>
                          <td style={totRS}>{fmtCost(totalCost)}</td>
                          <td style={totRS}>{fmtMil(totalCapRec)}</td>
                          <td style={totRS}>{fmtMil(totalFutRec)}</td>
                          <td style={totRS}>{fmtMil(totalFutVal)}</td>
                          <td style={{ ...totS, textAlign: "center" }} />
                          <td style={{ ...totS, textAlign: "center" }} />
                          <td style={{ ...totS, textAlign: "center" }}>{fmtMonYY(earliestFirst)}</td>
                          <td style={{ ...totS, textAlign: "center" }} />
                          <td style={{ ...totS, textAlign: "center" }}>{fmtMonYY(latestLast)}</td>
                          <td style={{ ...totS, textAlign: "center" }} />
                          <td style={{ ...totS, textAlign: "center" }} />
                          {canEditAssets && <td style={totS} />}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </TabsContent>

          {/* Fluxo de Caixa tab */}
          <TabsContent value="cashflow">
            {cashflowChartData.length === 0 ? (
              <div className="rounded-lg border border-border bg-card p-6">
                <p className="text-sm text-muted-foreground text-center py-8">
                  Nenhum cashflow registrado para este fundo.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Gráfico 1 — barras simples */}
                <div className="rounded-lg border border-border bg-card p-6">
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={cashflowChartData} margin={{ top: 8, right: 16, left: 24, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                      <XAxis dataKey="year" tick={{ fontSize: 12 }} className="fill-muted-foreground" tickLine={false} axisLine={false} />
                      <YAxis hide />
                      <Tooltip content={<FundCashflowTooltip />} />
                      {cashflowAssets.map((a, i) => (
                        <Bar
                          key={a.id}
                          dataKey={a.asset_name}
                          stackId="a"
                          fill={ASSET_COLORS[i % ASSET_COLORS.length]}
                          radius={i === cashflowAssets.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Gráfico 2 — com rótulos internos e totais acima */}
                <div className="rounded-lg border border-border bg-card p-6">
                  <ResponsiveContainer width="100%" height={440}>
                    <BarChart data={cashflowDetailData} margin={{ top: 72, right: 24, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                      <XAxis dataKey="year" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                      <YAxis hide />
                      <Tooltip content={<FundCashflowTooltip />} />
                      {cashflowAssets.map((a, i) => (
                        <Bar
                          key={a.id}
                          dataKey={a.asset_name}
                          stackId="a"
                          fill={ASSET_COLORS[i % ASSET_COLORS.length]}
                        >
                          <LabelList
                            dataKey={a.asset_name}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            content={((props: any) => {
                              const { x = 0, y = 0, width = 0, height = 0, value = 0 } = props;
                              if (!value || height < 26) return null;
                              const fv = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(Number(value));
                              return (
                                <g>
                                  <text x={x + width / 2} y={y + height / 2 - 6} textAnchor="middle" fill="white" fontSize={9} fontWeight={700}>
                                    {a.asset_name}
                                  </text>
                                  <text x={x + width / 2} y={y + height / 2 + 7} textAnchor="middle" fill="white" fontSize={9}>
                                    {fv}
                                  </text>
                                </g>
                              );
                            }) as any}
                          />
                        </Bar>
                      ))}
                      {/* Barra fantasma: posiciona rótulos Y/A/% acima de cada coluna */}
                      <Bar dataKey="_phantom" stackId="a" fill="transparent" isAnimationActive={false} legendType="none">
                        <LabelList
                          dataKey="_phantom"
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          content={((props: any) => {
                            const { x = 0, y = 0, width = 0, index = 0 } = props;
                            const d = cashflowDetailData[index] as Record<string, number> | undefined;
                            if (!d || !d._yearTotal) return null;
                            const fmt = (v: number) =>
                              new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v);
                            return (
                              <g>
                                <text x={x + width / 2} y={y - 48} textAnchor="middle" fontSize={10} fontWeight={700} fill="hsl(var(--foreground))">
                                  {`Y ${fmt(d._yearTotal)}`}
                                </text>
                                <text x={x + width / 2} y={y - 32} textAnchor="middle" fontSize={10} fontWeight={700} fill="hsl(var(--foreground))">
                                  {`A ${fmt(d._cumulative)}`}
                                </text>
                                <text x={x + width / 2} y={y - 16} textAnchor="middle" fontSize={10} fontWeight={700} fill="hsl(var(--foreground))">
                                  {`${Math.round(d._pct as number)}%`}
                                </text>
                              </g>
                            );
                          }) as any}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Investidores tab */}
          <TabsContent value="investors">
            {(() => {
              const thI: React.CSSProperties = {
                backgroundColor: "rgb(212, 163, 123)",
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "11px",
                padding: "6px 8px",
                whiteSpace: "nowrap",
                textAlign: "left",
              };
              const thIR: React.CSSProperties = { ...thI, textAlign: "right" };
              const tdI: React.CSSProperties = {
                fontSize: "12px",
                padding: "5px 8px",
                whiteSpace: "nowrap",
                borderBottom: "1px solid hsl(var(--border))",
              };
              const tdIR: React.CSSProperties = { ...tdI, textAlign: "right" };
              const totI: React.CSSProperties = {
                backgroundColor: "rgb(0, 56, 130)",
                color: "#e2eaf5",
                fontWeight: 700,
                fontSize: "12px",
                padding: "6px 8px",
                whiteSpace: "nowrap",
              };
              const totIR: React.CSSProperties = { ...totI, textAlign: "right" };

              let rowIdx = 0;

              return (
                <div className="rounded-lg border border-border overflow-x-auto max-w-[70%]">
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={thI}>Investor</th>
                        <th style={thIR}>BRL</th>
                        <th style={thIR}>USD</th>
                        <th style={thIR}>% Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {investorsGrouped.map(({ origin, investments: grpInvs }) => (
                        <React.Fragment key={origin}>
                          <tr style={{ backgroundColor: "hsl(var(--muted) / 0.3)" }}>
                            <td
                              colSpan={4}
                              style={{
                                ...tdI,
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "hsl(var(--muted-foreground))",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                padding: "4px 8px",
                              }}
                            >
                              {origin}
                            </td>
                          </tr>
                          {grpInvs.map((i) => {
                            const totalRow =
                              Number(i.amount_brl) +
                              (fund.usdbrl_rate ?? 0) * Number(i.amount_usd);
                            const pct = totalGeral > 0 ? (totalRow / totalGeral) * 100 : 0;
                            const bg = rowIdx++ % 2 === 0 ? "hsl(var(--card))" : "rgb(242, 242, 242)";
                            return (
                              <tr key={i.id} style={{ backgroundColor: bg }}>
                                <td style={{ ...tdI, fontWeight: 600, textTransform: "uppercase" }}>
                                  {clientName(i.client_id)}
                                </td>
                                <td style={tdIR}>{Number(i.amount_brl) ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(i.amount_brl)) : "—"}</td>
                                <td style={tdIR}>{Number(i.amount_usd) ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(i.amount_usd)) : "—"}</td>
                                <td style={tdIR}>{pct > 0 ? `${pct.toFixed(0)}%` : "—"}</td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                      {myInvestments.length > 0 && (
                        <tr>
                          <td style={{ ...totI, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Total ({myInvestments.length})
                          </td>
                          <td style={totIR}>{new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalBRL)}</td>
                          <td style={totIR}>{new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalUSD)}</td>
                          <td style={totIR}>100%</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </TabsContent>

          {/* Capital Calls tab */}
          <TabsContent value="capital-calls">
            <CapitalCallsTab fundId={fund.id} isAdmin={isAdmin} />
          </TabsContent>

          {/* Capital Distributed tab */}
          <TabsContent value="capital-distributions">
            <CapitalDistributionsTab fundId={fund.id} isAdmin={isAdmin} />
          </TabsContent>
        </Tabs>
      </div>

      <AssetFormDialog
        open={assetFormOpen}
        onOpenChange={setAssetFormOpen}
        asset={editAsset}
        funds={funds ?? []}
        defaultFundId={id}
      />

      <AssetCashflowDialog
        asset={cashflowAsset}
        open={cashflowOpen}
        onOpenChange={setCashflowOpen}
        readOnly={!canEditAssets}
      />
      <AlertDialog open={!!deleteAssetId} onOpenChange={() => setDeleteAssetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar remoção</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O ativo e seus cashflows serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAsset}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
